import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import api from "../../utils/api";
import { Shield, CheckCircle, XCircle, Clock, History } from "lucide-react";

const AdminBankDetails = () => {
  const [tab, setTab] = useState("pending"); // "pending" | "history"
  const [historyFilter, setHistoryFilter] = useState("Approved"); // "Approved" | "Rejected"

  const [bankQueue, setBankQueue] = useState([]);
  const [bankHistory, setBankHistory] = useState([]);
  const [selectedBankDetail, setSelectedBankDetail] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Fetch when tab or historyFilter changes
  useEffect(() => {
    if (tab === "pending") {
      fetchBankQueue();
    } else {
      fetchBankHistory(historyFilter);
    }
  }, [tab, historyFilter]);

  // Fetch pending Bank details queue (ADMIN endpoint)
  const fetchBankQueue = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/bank-details", {
        params: { status: "Pending" },
      });
      setBankQueue(res.data || []);
      console.log("/admin/bank-details?status=Pending:", res.data);
    } catch (err) {
      console.error("Failed to fetch Bank queue:", err?.response?.data || err);
      setBankQueue([]);
    }
    setLoading(false);
  };

  // Fetch Approved / Rejected Bank details for history (ADMIN endpoint)
  const fetchBankHistory = async (status) => {
    setLoading(true);
    try {
      const res = await api.get("/admin/bank-details", {
        params: { status },
      });
      setBankHistory(res.data || []);
      console.log(`/admin/bank-details?status=${status}:`, res.data);
    } catch (err) {
      console.error("Failed to fetch Bank history:", err?.response?.data || err);
      setBankHistory([]);
    }
    setLoading(false);
  };

  const handleApprove = async (bankdetailId, status) => {
    if (!bankdetailId) return;
    setProcessing(true);
    try {
      await api.post(`/admin/bank-details/${bankdetailId}/approve`, {
        status,
        remarks: remarks || undefined,
      });
      alert(`Bank details ${status.toLowerCase()} successfully!`);
      setSelectedBankDetail(null);
      setRemarks("");
      // refresh list
      if (tab === "pending") {
        fetchBankQueue();
      } else {
        fetchBankHistory(historyFilter);
      }
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to process bank details");
    }
    setProcessing(false);
  };

  const openBankCard = (item) => {
    setSelectedBankDetail(item);
    setRemarks("");
  };

  const closeBankCard = () => {
    if (processing) return;
    setSelectedBankDetail(null);
    setRemarks("");
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  const renderStatusBadge = (status) => {
    if (status === "Approved") {
      return (
        <span className="bg-success-light text-success text-xs font-semibold px-3 py-1 rounded-full">
          Approved
        </span>
      );
    }
    if (status === "Rejected") {
      return (
        <span className="bg-error-light text-error text-xs font-semibold px-3 py-1 rounded-full">
          Rejected
        </span>
      );
    }
    return (
      <span className="bg-warning-light text-warning text-xs font-semibold px-3 py-1 rounded-full">
        Pending
      </span>
    );
  };


  const renderDocs = (documentation) => {
    if (!documentation) {
      return (
        <p className="text-xs text-gray-500 col-span-full">
          No documents uploaded
        </p>
      );
    }

    const items = [
      { key: "bank_passbook", label: "Bank Passbook" },
      { key: "cancelled_cheque", label: "Cancelled Cheque" },
      { key: "bank_statement", label: "Bank Statement" },
      { key: "upi_screenshot", label: "UPI Screenshot" },
    ];

    const docsToShow = items.filter((item) => documentation[item.key]);

    if (docsToShow.length === 0) {
      return (
        <p className="text-xs text-gray-500 col-span-full">
          No documents uploaded
        </p>
      );
    }

    return docsToShow.map((doc) => (
      <a
        key={doc.key}
        href={documentation[doc.key]}
        target="_blank"
        rel="noreferrer"
        className="block"
      >
        <div className="border rounded-lg overflow-hidden">
          <img
            src={documentation[doc.key]}
            alt={doc.label}
            className="w-full h-28 object-cover"
          />
        </div>
        <p className="mt-1 text-xs text-gray-600">{doc.label}</p>
      </a>
    ));
  };

  return (
    <Layout>
      <div className="space-y-6 relative">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-gray-900">
              Bank / UPI Accounts
            </h1>
            <p className="text-gray-600 mt-1">
              {tab === "pending"
                ? `${bankQueue.length} pending verifications`
                : `${bankHistory.length} reviewed (${historyFilter})`}
            </p>
          </div>

          {/* Top Tabs */}
          <div className="flex gap-3 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setTab("pending")}
              className={`px-4 py-2 rounded-lg font-semibold flex items-center gap-2 ${
                tab === "pending"
                  ? "bg-white shadow text-primary"
                  : "text-gray-600 hover:text-primary"
              }`}
            >
              <Clock className="w-4 h-4" /> Pending
            </button>
            <button
              onClick={() => setTab("history")}
              className={`px-4 py-2 rounded-lg font-semibold flex items-center gap-2 ${
                tab === "history"
                  ? "bg-white shadow text-primary"
                  : "text-gray-600 hover:text-primary"
              }`}
            >
              <History className="w-4 h-4" /> Last Bank List
            </button>
          </div>
        </div>

        {/* === Pending Bank LIST === */}
        {tab === "pending" && (
          <>
            {bankQueue.length > 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {/* Header row */}
                <div className="grid grid-cols-5 gap-4 px-4 py-3 border-b bg-gray-50 text-xs font-semibold text-gray-600">
                  <div>Name</div>
                  <div>TAI ID</div>
                  <div>Type</div>
                  <div>Status</div>
                  <div>Phone</div>
                </div>

                {/* Rows */}
                {bankQueue.map((item) => (
                  <button
                    key={item.bankdetailId || item.id}
                    onClick={() => openBankCard(item)}
                    className="w-full text-left grid grid-cols-5 gap-4 px-4 py-3 border-b last:border-b-0 hover:bg-gray-50 focus:outline-none"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-gray-900">
                        {item.userName || item.name || "—"}
                      </span>
                      <span className="text-xs text-gray-500">
                        {item.userEmail || ""}
                      </span>
                    </div>
                    <div className="flex items-center text-sm font-mono text-gray-800">
                      {item.taiId || "—"}
                    </div>
                    <div className="flex items-center text-sm text-gray-800">
                      {item.type || "—"}
                    </div>
                    <div className="flex items-center">
                      {renderStatusBadge(item.status)}
                    </div>
                    <div className="flex items-center text-sm text-gray-800">
                      {item.userPhone || "—"}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="col-span-2 text-center py-12">
                <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">
                  No pending bank verifications
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  All bank / UPI details have been processed
                </p>
              </div>
            )}
          </>
        )}

        {/* === Bank History === */}
        {tab === "history" && (
          <div className="space-y-4">
            {/* Inner tabs: Approved / Rejected */}
            <div className="flex gap-2">
              <button
                onClick={() => setHistoryFilter("Approved")}
                className={`px-4 py-2 rounded-lg text-sm font-semibold ${
                  historyFilter === "Approved"
                    ? "bg-primary text-white"
                    : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Approved
              </button>
              <button
                onClick={() => setHistoryFilter("Rejected")}
                className={`px-4 py-2 rounded-lg text-sm font-semibold ${
                  historyFilter === "Rejected"
                    ? "bg-primary text-white"
                    : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                Rejected
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {bankHistory.length > 0 ? (
                bankHistory.map((item) => (
                  <div
                    key={item.bankdetailId || item.id}
                    className="bg-white rounded-lg border border-gray-200 p-6 cursor-pointer hover:shadow-sm"
                    onClick={() => openBankCard(item)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg">
                          {item.userName || item.name}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {item.userEmail}
                        </p>
                        <p className="text-sm text-gray-600">
                          {item.userPhone}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          TAI ID: {item.taiId || "—"}
                        </p>
                      </div>
                      {renderStatusBadge(item.status)}
                    </div>

                    <div className="space-y-3">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-1">
                          Account Type
                        </p>
                        <p className="font-mono text-sm text-gray-800">
                          {item.type || "—"}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-600 mb-1">Remarks</p>
                        <p className="font-mono text-sm text-gray-800">
                          {item.remarks || "—"}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500">
                        Updated:{" "}
                        {item.updatedAt
                          ? new Date(item.updatedAt).toLocaleString()
                          : "—"}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-2 text-center py-12">
                  <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 font-medium">
                    No {historyFilter.toLowerCase()} bank records yet
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Once verified, {historyFilter.toLowerCase()} bank / UPI
                    details will appear here
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* === DETAIL CARD (MODAL) === */}
        {selectedBankDetail && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={closeBankCard}
          >
            <div
              className="bg-white rounded-2xl shadow-xl max-w-3xl w-full mx-4 p-6 relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
                onClick={closeBankCard}
                disabled={processing}
              >
                <XCircle className="w-6 h-6" />
              </button>

              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {selectedBankDetail.userName ||
                      selectedBankDetail.name ||
                      "Bank Details"}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {selectedBankDetail.userEmail}
                  </p>
                  <p className="text-sm text-gray-600">
                    {selectedBankDetail.userPhone}
                  </p>
                </div>
                <div className="text-right space-y-1">
                  {renderStatusBadge(selectedBankDetail.status)}
                  <p className="text-xs text-gray-500">
                    User ID: {selectedBankDetail.userId || "—"}
                  </p>
                  <p className="text-xs text-gray-500">
                    TAI ID: {selectedBankDetail.taiId || "—"}
                  </p>
                </div>
              </div>

              {/* Basic info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Account Type</p>
                  <p className="font-mono text-sm font-semibold">
                    {selectedBankDetail.type || "—"}
                  </p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Account Holder</p>
                  <p className="text-sm text-gray-800">
                    {selectedBankDetail.name || "—"}
                  </p>
                </div>

                {selectedBankDetail.type === "BANK" && (
                  <>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">Bank Name</p>
                      <p className="text-sm text-gray-800">
                        {selectedBankDetail.bankName || "—"}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-600 mb-1">
                        Branch / IFSC
                      </p>
                      <p className="text-sm text-gray-800">
                        {selectedBankDetail.branchName || "—"}
                      </p>
                      <p className="font-mono text-xs text-gray-700">
                        {selectedBankDetail.ifsc || "—"}
                      </p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg md:col-span-2">
                      <p className="text-xs text-gray-600 mb-1">
                        Account Number
                      </p>
                      <p className="font-mono text-sm font-semibold">
                        {selectedBankDetail.accountNumber}
                      </p>
                    </div>
                  </>
                )}

                {selectedBankDetail.type === "UPI" && (
                  <div className="bg-gray-50 p-3 rounded-lg md:col-span-2">
                    <p className="text-xs text-gray-600 mb-1">UPI ID</p>
                    <p className="font-mono text-sm font-semibold">
                      {selectedBankDetail.upiId || "—"}
                    </p>
                  </div>
                )}

                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Created At</p>
                  <p className="text-sm text-gray-800">
                    {selectedBankDetail.createdAt
                      ? new Date(
                          selectedBankDetail.createdAt
                        ).toLocaleString()
                      : "—"}
                  </p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Updated At</p>
                  <p className="text-sm text-gray-800">
                    {selectedBankDetail.updatedAt
                      ? new Date(
                          selectedBankDetail.updatedAt
                        ).toLocaleString()
                      : "—"}
                  </p>
                </div>
              </div>

              {/* Documents */}
              <div className="mb-4">
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  Documents
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {renderDocs(selectedBankDetail.documentation)}
                </div>
              </div>

              {/* Remarks + Actions (only for Pending) */}
              {selectedBankDetail.status === "Pending" && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Remarks (Optional)
                    </label>
                    <textarea
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Add remarks for rejection or notes..."
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() =>
                        handleApprove(
                          selectedBankDetail.bankdetailId ||
                            selectedBankDetail.id,
                          "Approved"
                        )
                      }
                      disabled={processing}
                      className="flex-1 bg-success hover:bg-success-dark text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:bg-gray-400"
                    >
                      <CheckCircle className="w-5 h-5" />
                      Approve
                    </button>
                    <button
                      onClick={() =>
                        handleApprove(
                          selectedBankDetail.bankdetailId ||
                            selectedBankDetail.id,
                          "Rejected"
                        )
                      }
                      disabled={processing}
                      className="flex-1 bg-error hover:bg-error-dark text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:bg-gray-400"
                    >
                      <XCircle className="w-5 h-5" />
                      Reject
                    </button>
                  </div>
                </>
              )}

              {selectedBankDetail.status !== "Pending" && (
                <p className="mt-3 text-xs text-gray-500">
                  These bank details are already{" "}
                  {selectedBankDetail.status}. Actions are disabled.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminBankDetails;
